import React, { useEffect, useState } from 'react';
import {
    Alert,
    CircularProgress,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import BlockWithTitle from '../../components/atom/BlockWithTitle';
import { useGetTicketOptions } from '@openapi/ticket-options/ticket-options';
import { useGetTimeslotPrices } from '@openapi/timeslots/timeslots';

const key = (ticketTypeId, ticketOptionId) => `${ticketTypeId}:${ticketOptionId}`;

/**
 * Editable ticket type x ticket option price grid for a single timeslot.
 *
 * Each cell overrides the show-level default price (shown as placeholder). Empty
 * cells store no override and fall back to the default at booking time. The full
 * set of overrides is pushed up to Formik as `values.timeslotPrices` (array of
 * { ticketTypeId, ticketOptionId, price } in cents) so the parent saves it via
 * the PUT /timeslots/{id}/prices endpoint.
 */
function TimeslotPriceMatrix({ showId, ticketTypes, timeslotId, setFieldValue }) {
    const { t } = useTranslation();

    const { data: ticketOptionsData, isLoading: loadingOptions } = useGetTicketOptions({
        optionalId: showId,
        page: 0,
        pageSize: 1000,
    });
    const ticketOptions = ticketOptionsData?.ticketOptions || [];

    const { data: pricesData, isLoading: loadingPrices } = useGetTimeslotPrices(
        Number(timeslotId),
        {
            query: { enabled: !!timeslotId },
        },
    );

    // Local edit state: `${ttId}:${toId}` -> price string in the display currency.
    const [cells, setCells] = useState({});

    useEffect(() => {
        if (!pricesData?.prices) return;
        const next = {};
        pricesData.prices.forEach((p) => {
            next[key(p.ticketTypeId, p.ticketOptionId)] = String(p.price / 100);
        });
        setCells(next);
        // Seed Formik with the already-saved overrides so a save that doesn't
        // touch the matrix preserves them. Without this, PUT /prices replaces
        // the full set with an empty array and wipes the existing overrides.
        setFieldValue('timeslotPrices', pricesData.prices);
    }, [pricesData, setFieldValue]);

    // Push the current overrides up to Formik whenever a cell changes.
    const syncToForm = (nextCells) => {
        const prices = Object.entries(nextCells)
            .filter(([, value]) => value !== '' && value !== null && value !== undefined)
            .map(([cellKey, value]) => {
                const [ticketTypeId, ticketOptionId] = cellKey.split(':').map(Number);
                return {
                    ticketTypeId,
                    ticketOptionId,
                    price: Math.round(parseFloat(value) * 100),
                };
            })
            .filter((p) => Number.isFinite(p.price) && p.price >= 0);
        setFieldValue('timeslotPrices', prices);
    };

    const updateCell = (ticketTypeId, ticketOptionId, value) => {
        const next = { ...cells, [key(ticketTypeId, ticketOptionId)]: value };
        setCells(next);
        syncToForm(next);
    };

    if (loadingOptions || loadingPrices) {
        return (
            <BlockWithTitle title={t('timeslots.priceMatrix.title')}>
                <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                    <CircularProgress />
                </div>
            </BlockWithTitle>
        );
    }

    if (!ticketTypes?.length || !ticketOptions.length) {
        return (
            <BlockWithTitle title={t('timeslots.priceMatrix.title')}>
                <Alert severity="info">{t('timeslots.priceMatrix.noData')}</Alert>
            </BlockWithTitle>
        );
    }

    return (
        <BlockWithTitle title={t('timeslots.priceMatrix.title')}>
            <Alert severity="info" sx={{ mb: 2 }}>
                {t('timeslots.priceMatrix.info')}
            </Alert>
            <div style={{ overflowX: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('timeslots.priceMatrix.ticketType')}</TableCell>
                            {ticketOptions.map((option) => (
                                <TableCell key={option.id}>{option.name}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {ticketTypes.map((ticketType) => {
                            const defaults = ticketType.priceMatrix || {};
                            return (
                                <TableRow key={ticketType.id}>
                                    <TableCell>
                                        <strong>{ticketType.name}</strong>
                                    </TableCell>
                                    {ticketOptions.map((option) => {
                                        const cellKey = key(ticketType.id, option.id);
                                        const defaultCents = defaults[option.name];
                                        const placeholder =
                                            defaultCents !== undefined && defaultCents !== null
                                                ? String(defaultCents / 100)
                                                : t('timeslots.priceMatrix.noPrice');
                                        return (
                                            <TableCell key={option.id}>
                                                <TextField
                                                    fullWidth
                                                    type="number"
                                                    size="small"
                                                    value={cells[cellKey] ?? ''}
                                                    onChange={(ev) =>
                                                        updateCell(
                                                            ticketType.id,
                                                            option.id,
                                                            ev.target.value,
                                                        )
                                                    }
                                                    inputProps={{ min: 0, step: 0.01 }}
                                                    placeholder={placeholder}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                $
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        </BlockWithTitle>
    );
}

export default TimeslotPriceMatrix;
